import { useMemo } from 'react';
import { QueryKey } from '@fabric-ui/utils';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { isValid } from 'date-fns';
import { gql } from 'graphql-request';
import { useSnackbar } from 'notistack';
import { useAvContext } from '../../context/AvContextProvider';
import useHandleError from '../../hooks/useHandleError';
import useQuerySql, { QUERY_SQL_FRAGMENT } from '../../hooks/useQuerySql';
import { UseQuerySqlProps } from '../../hooks/useQuerySql.types';
import { FeatureFlags } from '../../types';
import { Filter } from '../../types/filter.types';
import { QueryOrigin } from '../../types/query.types';
import { field } from '../../utils/filter/filter-builder';
import { mergeAnd } from '../../utils/filter/merge-filter';
import { flattenObject, generateOption, getNetworkErrorCode, isNullOrUndefined } from '../../utils/utils';
import { LOGS_CONST } from '../LogsBeta/Logs.const';
import {
  createFirstPartyLogsPieSql,
  createFirstPartySearchLogsTotalsSql,
  getViewProjectionsFieldQuery,
} from './get-view-projections-query';
import { ANY_VALUE, getAliasString, getInterval, getPieSlicerFilter, LegalIntervalUnit, queriesList, QueryObj } from './logs.utils';
import { DEFAULT_LOGS_TABLE_NAME } from './NewVersion/useSearchLogsSqlState.utils';
import { LogQuery, LogsQueryCategoryType, QueryType, Tab } from './types';
import { useGetSourcesList } from './useGetSourcesList';
import { transformLegacySQLDialect } from './utils/transformLegacySQLDialect';

const dataExplorationOrigin = 'DATA_EXPLORATION';

// eslint-disable-next-line custom-rules/no-unamed-gql
const createSqlGql = (name: string) => gql`
  query ${name}($req: Json!) {
    querySql(value: $req) {
      ...QuerySqlFields
    }
  }

  ${QUERY_SQL_FRAGMENT}
`;

export const useAccountFacets = ({ enabled, onSuccess }: { enabled?: boolean; onSuccess }) => {
  const { api } = useAvContext();
  return useQuery({
    queryKey: [QueryKey.AccountFacets],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      api(QUERY_ACCOUNT_FACETS, {
        options: { req: {} },
        signal,
        onSuccess: ({ data, errors }) => onSuccess(errors ? [] : data.logSearchAccountFacetList.fields),
      }),
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const prepareDateFilter = (date: Date[], isFirstParty: boolean, table: string) =>
  field(isFirstParty ? `${table}.event_timestamp` : 'TIME').date.between(date[0], date[1]);

export const prepareAdditionalFilters = ({
  isFirstParty,
  table,
  date,
  pieSlicer,
  filter,
}: {
  isFirstParty: boolean;
  table: string;
  date: [Date, Date];
  pieSlicer: any[];
  filter: Filter | null;
}) => {
  const dateFilter = prepareDateFilter(date, isFirstParty, table);
  return filter ? mergeAnd(filter, dateFilter, ...getPieSlicerFilter(pieSlicer)) : mergeAnd(dateFilter, ...getPieSlicerFilter(pieSlicer));
};

export const useAdvancedLogsQuery = ({
  query,
  date,
  enabled,
  lastSearch,
  additionalFilter,
  ...options
}: {
  query: LogQuery;
  date: Date[];
  lastSearch?: Date;
  enabled?: boolean;
} & Omit<UseQuerySqlProps, 'key' | 'sql' | 'origin'>) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: sources, isLoading } = useGetSourcesList();

  const sqlTable = query?.match(/from\s+([a-zA-Z0-9_.]+)/i)?.at(1) || null;
  const isFirstPartySqlTable = sqlTable && sources?.some(source => source.projectionIdName === sqlTable);

  return useQuerySql({
    gql: createSqlGql('advancedLogsQuerySql'),
    key: `${date}${query}${lastSearch}${additionalFilter}`,
    sql: query || '',
    origin: isFirstPartySqlTable ? 'INTERACTIVE_QUERY' : dataExplorationOrigin,
    options: { enabled: enabled && !isLoading },
    debounceQueries: false,
    additionalFilter,
    onSuccess: data =>
      data.map(({ DOCUMENT, ALL, id, ...v }, index) => ({
        avalorRowId: `${index}${id || ''}`,
        data: flattenObject({ obj: { ...DOCUMENT, ...ALL, ...v } }),
        raw: { ...(DOCUMENT ? { DOCUMENT } : {}), ...v },
      })),
    onError: error =>
      enqueueSnackbar((error?.[0]?.extensions || error).message, {
        variant: 'error',
      }),
    ...options,
  });
};

export const useVeryNewLogsTotals = ({
  queryType,
  query,
  date,
  isValidTime,
  lastSearch,
  pieSlicer,
  isFirstParty,
  table,
  additionalFilter,
}: {
  queryType: QueryType;
  query: LogQuery;
  date: Date[];
  isValidTime: boolean;
  lastSearch?: Date;
  pieSlicer: any[];
  isFirstParty: boolean;
  table: string;
  additionalFilter: Filter;
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const isAdvancedQuery = queryType === QueryType.Advanced && query !== undefined;
  const sql = isFirstParty
    ? createFirstPartySearchLogsTotalsSql(table)
    : !isValidTime || isAdvancedQuery
      ? ''
      : `SELECT count(*) as totals FROM logs`;

  const enabled = Boolean(lastSearch && sql && !isAdvancedQuery);
  return useQuerySql({
    gql: createSqlGql('searchLogsTotalQuerySql'),
    key: `${date}${query}${lastSearch}${pieSlicer.map(({ fieldName, value }) => `${fieldName}${value}`)}`,
    sql,
    origin: isFirstParty ? 'INTERACTIVE_QUERY' : dataExplorationOrigin,
    options: {
      enabled,
    },
    debounceQueries: false,
    additionalFilter,
    onSuccess: data => data[0].totals,
    onError: error =>
      enqueueSnackbar((error?.[0]?.extensions || error).message, {
        variant: 'error',
      }),
  });
};

const createFirstPartyHistogramSql = (
  table: string,
  timestampField: string,
  from: Date,
  to: Date,
  interval: ReturnType<typeof getInterval>
) => {
  const [amount, legalIntervalUnit] = interval.display.split(' ');
  const unitMap = {
    Days: 'DAY',
    Hours: 'HOUR',
    Minutes: 'MINUTE',
    Seconds: 'SECOND',
  } as const satisfies Record<LegalIntervalUnit, string>;
  const unit = unitMap[legalIntervalUnit as LegalIntervalUnit];

  return `SELECT HISTOGRAM_BUCKET(${timestampField}, '${from.toISOString()}', '${to.toISOString()}', ${amount}, '${unit}') AS date,
    count(${timestampField}) as count
    FROM ${table}
    where ${timestampField} >= '${from.toISOString()}'
    and ${timestampField} <= '${to.toISOString()}'
    group by date
    order by date`;
};

const createHistogramSql = (from: Date, to: Date, interval: ReturnType<typeof getInterval>) =>
  `SELECT TIME_BUCKET('${from.toISOString()}', '${to.toISOString()}', '${interval.display}') as date, COUNT(*) as count FROM logs GROUP BY date ORDER BY date asc`;

export const fillHistogramDataGap = ([from, to]: [Date, Date], intervalMs: number, rows: { count: number; date: string }[]) => {
  const startMs = from.getTime();
  const endMs = to.getTime();
  if (!(endMs >= startMs) || intervalMs <= 0) {
    return [];
  }
  const totalBuckets = Math.floor((endMs - startMs) / intervalMs) + 1;
  const byBucket = rows.reduce((map, row) => {
    const timestamp = new Date(row.date).getTime();
    const index = Math.floor((timestamp - startMs) / intervalMs);
    if (index >= 0 && index < totalBuckets) {
      const b = startMs + index * intervalMs;
      map.set(b, (map.get(b) ?? 0) + Number(row.count ?? 0));
    }
    return map;
  }, new Map<number, number>());
  return Array.from({ length: totalBuckets }, (_, i) => {
    const ts = startMs + i * intervalMs;
    return { date: new Date(startMs + i * intervalMs), count: byBucket.get(ts) ?? 0 };
  });
};

export const useLogsBarQuery = ({
  date,
  queryType,
  query,
  isValidTime,
  enabled = true,
  lastSearch,
  additionalFilter,
  table,
  isFirstParty,
}: {
  queryType: QueryType;
  query: LogQuery;
  date: Date[];
  isValidTime: boolean;
  enabled?: boolean;
  lastSearch?: Date;
  additionalFilter: Filter;
  table: string;
  isFirstParty: boolean;
}) => {
  const isSql = queryType === QueryType.Advanced && query !== undefined;
  const interval = getInterval(100, [date[0], date[1]]);

  const sql =
    !isSql && isValidTime
      ? isFirstParty
        ? createFirstPartyHistogramSql(table, `${table}.event_timestamp`, date[0], date[1], interval)
        : createHistogramSql(date[0], date[1], interval)
      : '';

  return useQuerySql({
    gql: createSqlGql('searchLogsBarQuerySql'),
    key: `${query}${lastSearch}`,
    sql,
    origin: isFirstParty ? 'INTERACTIVE_QUERY' : dataExplorationOrigin,
    additionalFilter,
    options: { enabled: !isSql && isValidTime && enabled && !!lastSearch },
    debounceQueries: false,
    onSuccess: data =>
      isFirstParty
        ? fillHistogramDataGap([date[0], date[1]], interval.valueMillis, data)
        : data.map(({ date, count }) => ({
            date: isFirstParty ? new Date(date) : new Date(`${date}.000Z`).toLocaleString(),
            count,
          })),
  });
};

export const useLogsPieQuery = ({
  query,
  queryType,
  date,
  groupByField,
  metric,
  top,
  isValidTime,
  enabled = true,
  lastSearch,
  isFirstParty,
  table,
  filter,
}: {
  query: LogQuery;
  queryType: QueryType;
  date: Date[];
  groupByField: string | null;
  metric: string | null;
  top: number;
  isValidTime: boolean;
  enabled?: boolean;
  lastSearch?: Date;
  isFirstParty: boolean;
  table: string;
  filter: Filter | null;
}) => {
  const isAdvancedQuery = queryType === QueryType.Advanced && query !== undefined;
  const alias = getAliasString(groupByField || '');

  const isFirstPartySql = isFirstParty && groupByField;
  const timestampField = isFirstParty ? `${table}.event_timestamp` : 'TIME';
  const sql = isFirstPartySql
    ? createFirstPartyLogsPieSql(table, groupByField, alias, top)
    : !isAdvancedQuery && isValidTime
      ? `SELECT  ${
          groupByField
            ? `coalesce(${groupByField
                .split('.')
                .map(word => `"${word}"`)
                .join('.')}, 'null') AS ${alias},`
            : ''
        } ${metric ? `COUNT_DISTINCT(${metric})` : 'COUNT(*)'} as count FROM logs
            ${groupByField ? `GROUP BY ${alias}` : ''} ORDER BY count DESC LIMIT ${top}`
      : '';

  const enableQuery = Boolean(enabled && isValidTime && lastSearch);

  return useQuerySql({
    gql: createSqlGql('searchLogsPieQuerySql'),
    key: `${date}${sql}${lastSearch}`,
    sql,
    origin: isFirstPartySql ? 'INTERACTIVE_QUERY' : dataExplorationOrigin,
    additionalFilter: {
      and: {
        operands: [
          {
            expression: {
              fieldName: timestampField,
              dateCondition: {
                between: { values: date },
              },
            },
          },
          ...(!isAdvancedQuery && query !== undefined && filter ? [filter] : []),
        ],
      },
    },
    options: {
      placeholderData: keepPreviousData,
      enabled: enableQuery,
    },
    debounceQueries: false,
    onSuccess: data => {
      if (!groupByField) {
        return data;
      }
      const formattedField = getAliasString(groupByField || '');
      return data.reduce((acc, curr) => {
        const { [formattedField]: value } = curr;
        return [
          ...acc,
          {
            ...curr,
            [formattedField]: typeof value === 'object' ? JSON.stringify(value) : value.toString(),
          },
        ];
      }, []);
    },
  });
};

export const useGetValuesOptions = ({
  fieldName,
  enabled,
  autocompleteFilter,
}: {
  fieldName?: string | null;
  enabled: boolean;
  autocompleteFilter?: Date[];
}) => {
  const { api, featureFlags } = useAvContext();
  const { data: viewProjectionsFields, isLoading } = useQuery(getViewProjectionsFieldQuery({ api }));
  const logsTableName = featureFlags[FeatureFlags.LogsPageBetaMigration]
    ? LOGS_CONST.EXTERNAL_PROJECTION_TABLE
    : DEFAULT_LOGS_TABLE_NAME.toLocaleLowerCase();
  const viewProjectionId = viewProjectionsFields
    ? Object.keys(viewProjectionsFields).find(projId => projId && projId === fieldName?.split('.').at(0))
    : null;

  const isViewProjection = !!viewProjectionId;

  const isValidTime = useMemo(() => autocompleteFilter?.every(date => isValid(date)), [autocompleteFilter]);
  const sql = `select distinct ${fieldName} from ${isViewProjection ? viewProjectionId : logsTableName} limit 100`;

  const sourcesQuery = useGetSourcesList();

  const origin =
    isViewProjection || featureFlags[FeatureFlags.LogsPageBetaMigration] ? QueryOrigin.INTERACTIVE_QUERY : QueryOrigin.DATA_EXPLORATION;

  const valuesOptions = useQuerySql({
    sql,
    key: `${fieldName}${autocompleteFilter}`,
    origin,
    ...(autocompleteFilter
      ? {
          additionalFilter: {
            and: {
              operands: [
                {
                  expression: {
                    fieldName: isViewProjection ? `${viewProjectionId}.event_timestamp` : 'TIME',
                    dateCondition: { between: { values: autocompleteFilter } },
                  },
                },
              ],
            },
          },
        }
      : {}),
    options: {
      enabled: !isLoading && !!isValidTime && !!fieldName && fieldName !== ANY_VALUE && fieldName !== 'source' && enabled,
      gcTime: 0,
    },
    onSuccess: data =>
      data.reduce(
        (acc, { [fieldName!]: value }) =>
          (Array.isArray(value) ? value.length : !isNullOrUndefined(value))
            ? [...acc, generateOption(typeof value === 'object' ? JSON.stringify(value) : value.toString())]
            : acc,
        []
      ),
  });

  return {
    data:
      fieldName === 'source'
        ? sourcesQuery.data?.filter(s => !s.isFirstParty).map(s => generateOption(s.mappedSourceName))
        : valuesOptions.data,
    isLoading: fieldName === 'source' ? sourcesQuery.isLoading : valuesOptions.isLoading,
  };
};

const RECENT_LOGS_QUERIES_STORAGE_KEY = 'recentLogsQueries';
export const useRecentQueries = () => {
  const { accountId } = useAvContext();

  const recentQueriesString = localStorage.getItem(RECENT_LOGS_QUERIES_STORAGE_KEY);
  const recentQueries = recentQueriesString ? JSON.parse(recentQueriesString) : { [accountId]: [] };
  const recentQueriesPerAccount = Array.isArray(recentQueries) ? { [accountId]: recentQueries } : recentQueries;
  const recentQueriesByAccount = recentQueriesPerAccount[accountId] || [];

  const addRecentQuery = query => {
    if (query) {
      const newRecentQueriesByAccount = [...new Set([query, ...recentQueriesByAccount])].slice(0, 5);
      localStorage.setItem(
        RECENT_LOGS_QUERIES_STORAGE_KEY,
        JSON.stringify({ ...recentQueriesPerAccount, [accountId]: newRecentQueriesByAccount })
      );
    }
  };
  return { recentQueries: recentQueriesByAccount, addRecentQuery };
};

export const useLogsQueries = () => {
  const { api } = useAvContext();
  return useQuery({
    queryKey: [QueryKey.LogsQueries],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      api(GET_LOGS_QUERY, {
        signal,
        onSuccess: ({ data }) =>
          data.findLogsQueries?.map(q =>
            q.queryType === QueryType.Advanced
              ? { ...q, table: q.tableName }
              : { ...q, table: q.tableName, query: transformLegacySQLDialect(q.query || '') }
          ),
      }),
  });
};

export const useLogsQueriesCategory = () => {
  const { api } = useAvContext();
  return useQuery({
    queryKey: [QueryKey.LogsQueriesCategory],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      api(GET_LOGS_QUERY_CATEGORY, { signal, onSuccess: ({ data }) => data.findLogsQueriesCategory }),
  });
};

export const useUpdateLogsQuery = () => {
  const { api } = useAvContext();
  const { refetch } = useLogsQueries();
  const { enqueueSnackbar } = useSnackbar();

  const { refetch: refetchCategory } = useLogsQueriesCategory();
  return useMutation({
    mutationFn: ({
      isCreate,
      activeDraftTab,
      onSuccess = d => d,
    }: {
      isCreate: boolean;
      activeDraftTab: Tab;
      onSuccess?: (id) => void;
    }) => {
      const { tabId, title, query, queryType, table } = activeDraftTab;
      const logsQuery = {
        ...(isCreate ? {} : { id: tabId }),
        name: title,
        query: query || '',
        queryType,
        ...(activeDraftTab.categoryName
          ? { logsQueryCategory: { name: activeDraftTab.categoryName, id: activeDraftTab.categoryId || undefined } }
          : {}),
        tableName: table,
      };
      return api(isCreate ? CREATE_LOGS_QUERY : UPDATE_LOGS_QUERY, {
        options: { logsQuery },
        onSuccess: ({ data }) => {
          onSuccess(data);
          refetch();
          refetchCategory();
          enqueueSnackbar(`Successfully ${isCreate ? 'Created' : 'Updated'} Query`, { variant: 'success' });
        },
      });
    },
  });
};

export const useUpdateLogsQueryCategory = () => {
  const { api, featureFlags } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();
  const { refetch: refetchCategory } = useLogsQueriesCategory();
  const handleError = useHandleError(featureFlags[FeatureFlags.ShowServerErrorInToast]);

  return useMutation({
    mutationFn: ({
      category,
      onSuccess = d => d,
      isCreate,
    }: {
      onSuccess?: (id) => void;
      category: LogsQueryCategoryType;
      isCreate: boolean;
    }) =>
      api(isCreate ? CREATE_LOGS_QUERY_CATEGORY : UPDATE_LOGS_QUERY_CATEGORY, {
        options: { logsQueryCategoryDto: category },
        onSuccess: ({ data }) => {
          onSuccess(data);
          refetchCategory();
          enqueueSnackbar(`Successfully Created Query Category`, { variant: 'success' });
        },
        muteErrors: true,
      }).catch(e => {
        if (getNetworkErrorCode(e) === 400) {
          console.warn(e);
          enqueueSnackbar(e.message, { variant: 'error' });
        } else {
          handleError(e);
        }
      }),
  });
};

export const useDeleteLogsQuery = onSuccess => {
  const { api } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (ids: string[]) =>
      api(DELETE_LOGS_QUERY, {
        options: { ids },
        onSuccess: () => {
          enqueueSnackbar(`Successfully Deleted Query`, { variant: 'success' });
          onSuccess();
        },
      }),
  });
};
export const useDeleteLogsQueryCategory = onSuccess => {
  const { api } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, shouldDeleteRelatedQueries }: { id: string; shouldDeleteRelatedQueries: string }) =>
      api(DELETE_LOGS_QUERY_CATEGORY, {
        options: { id, shouldDeleteRelatedQueries },
        onSuccess: () => {
          enqueueSnackbar(`Successfully Deleted Category`, { variant: 'success' });
          onSuccess();
        },
      }),
  });
};

export const useBulkUpdateLogsQueryCategory = onSuccess => {
  const { api } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ ids, logsQueryCategoryId }: { ids: string[]; logsQueryCategoryId: string }) =>
      api(BULK_UPDATE_LOGS_QUERY_CATEGORY, {
        options: { ids, logsQueryCategoryId },
        onSuccess: () => {
          enqueueSnackbar(`Successfully Update Logs Queries`, { variant: 'success' });
          onSuccess();
        },
      }),
  });
};

export const topXQueries = 5;
export const useGetTop5SuggestionQueries = () => {
  const { data: sourcesObjList } = useGetSourcesList();
  const sources = sourcesObjList?.map(source => source.name) || [];

  const top5SuggestionQueries = useMemo(() => {
    const suggestionQueries = queriesList.reduce((acc, queryObj) => {
      if (sources.includes(queryObj.source) && acc.length < topXQueries) {
        return [...acc, queryObj];
      }
      return acc;
    }, [] as QueryObj[]);
    if (suggestionQueries.length === topXQueries) {
      return suggestionQueries;
    }
    const restSuggestionQueries = queriesList.reduce((acc, queryObj) => {
      if (!sources.includes(queryObj.source) && acc.length < topXQueries - suggestionQueries.length) {
        return [...acc, queryObj];
      }
      return acc;
    }, [] as QueryObj[]);
    return [...suggestionQueries, ...restSuggestionQueries];
  }, [sources]);
  return top5SuggestionQueries.map(({ query }) => query);
};

const QUERY_ACCOUNT_FACETS = gql`
  query logSearchAccountFacetList($req: Json!) {
    logSearchAccountFacetList(value: $req) {
      fields
    }
  }
`;

const GET_LOGS_QUERY_CATEGORY = gql`
  query findLogsQueriesCategory {
    findLogsQueriesCategory {
      id
      name
    }
  }
`;

const GET_LOGS_QUERY = gql`
  query findLogsQueries {
    findLogsQueries {
      id
      name
      description
      query
      queryType
      updatedByUserId
      updatedAt
      tableName
      logsQueryCategory {
        id
        name
      }
    }
  }
`;

const CREATE_LOGS_QUERY_CATEGORY = gql`
  mutation createLogsQueryCategory($logsQueryCategoryDto: LogsQueryCategoryInput!) {
    createLogsQueryCategory(logsQueryCategoryDto: $logsQueryCategoryDto) {
      id
    }
  }
`;

const BULK_UPDATE_LOGS_QUERY_CATEGORY = gql`
  mutation bulkUpdateLogsQueryCategory($ids: [String]!, $logsQueryCategoryId: String) {
    bulkUpdateLogsQueryCategory(ids: $ids, logsQueryCategoryId: $logsQueryCategoryId)
  }
`;

const UPDATE_LOGS_QUERY_CATEGORY = gql`
  mutation updateLogsQueryCategory($logsQueryCategoryDto: LogsQueryCategoryInput!) {
    updateLogsQueryCategory(logsQueryCategoryDto: $logsQueryCategoryDto) {
      id
    }
  }
`;

const DELETE_LOGS_QUERY_CATEGORY = gql`
  mutation deleteLogsQueryCategory($id: String!, $shouldDeleteRelatedQueries: Boolean) {
    deleteLogsQueryCategory(id: $id, shouldDeleteRelatedQueries: $shouldDeleteRelatedQueries)
  }
`;

const DELETE_LOGS_QUERY = gql`
  mutation deleteLogsQuery($ids: [String]!) {
    deleteLogsQuery(ids: $ids)
  }
`;

const CREATE_LOGS_QUERY = gql`
  mutation createLogsQuery($logsQuery: LogsQueryInput!) {
    createLogsQuery(logsQuery: $logsQuery) {
      id
    }
  }
`;

const UPDATE_LOGS_QUERY = gql`
  mutation updateLogsQuery($logsQuery: LogsQueryInput!) {
    updateLogsQuery(logsQuery: $logsQuery) {
      id
    }
  }
`;
